// [PURPOSE]: Tutorial system state management — tracks active tutorial, current step, completed modules
// [WHY]: Centralized context lets any component trigger/advance/skip tutorials without prop drilling
// [INTEGRATION]: Consumed by TutorialSpotlight, TutorialPrompt, WelcomePopup, and App.jsx
// [CREATED]: February 28, 2026 — Contextual Tutorial System redesign

import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { TUTORIAL_MODULES, TAB_TO_MODULE, MODULE_TO_TAB, PREMIUM_TUTORIAL_MODULES } from '../data/tutorialModules';
import { usePremiumAccess } from '../hooks/usePremiumAccess';
import { safeLocalStorageSet } from '../utils/storageValidator';
// [S6]: Analytics tracking for tutorial engagement metrics
import { trackEvent } from '../services/analytics';
// [FIX APR 2026 Phase 3C.1]: Emit tab-switch requests when a tutorial starts.
import { systemBus, EVENTS } from '../services/systemBus';
// [P1.4 — Apr 29 2026]: Firebase-backed tutorial state so completion follows
//   the user's account, not the device's localStorage. User report: tutorials
//   re-prompted on every login from a private window.
import { useAuth } from './AuthContext';
import {
  loadTutorialFromFirebase,
  saveTutorialToFirebase,
  mergeTutorialState,
} from '../services/tutorialPersistence';

// ========================================
// CONSTANTS
// ========================================

const STORAGE_KEY = 'tabletopInventory_tutorialState';
const OLD_STORAGE_KEY = 'tutorialComplete'; // Legacy key from old TutorialOverlay

// ========================================
// CONTEXT
// ========================================

const TutorialContext = createContext(null);

// ========================================
// HELPERS
// ========================================

/**
 * Load tutorial state from localStorage with migration from old format
 * [WHY]: Returning users who completed the old tutorial shouldn't see tutorials again
 */
function loadTutorialState() {
  try {
    // Check for new format first
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        completedModules: parsed.completedModules || {},
        declinedModules: parsed.declinedModules || {},
      };
    }

    // Migrate from old format: if old tutorial was completed, mark everything done
    const oldComplete = localStorage.getItem(OLD_STORAGE_KEY);
    if (oldComplete === 'true') {
      const allComplete = {};
      Object.keys(TUTORIAL_MODULES).forEach(id => {
        allComplete[id] = true;
      });
      const migrated = { completedModules: allComplete, declinedModules: {} };
      safeLocalStorageSet(STORAGE_KEY, JSON.stringify(migrated));
      localStorage.removeItem(OLD_STORAGE_KEY);
      return migrated;
    }

    // Brand new user
    return { completedModules: {}, declinedModules: {} };
  } catch (e) {
    console.warn('[TutorialContext] Failed to load state:', e);
    return { completedModules: {}, declinedModules: {} };
  }
}

/**
 * Save tutorial state to localStorage
 */
function saveTutorialState(state) {
  try {
    safeLocalStorageSet(STORAGE_KEY, JSON.stringify({
      completedModules: state.completedModules,
      declinedModules: state.declinedModules,
    }));
  } catch (e) {
    console.warn('[TutorialContext] Failed to save state:', e);
  }
}

// ========================================
// PROVIDER
// ========================================

export function TutorialProvider({ children }) {
  const hasPremium = usePremiumAccess();
  // [P1.4]: currentUser drives the dual-write: when authenticated, every state
  //   change ALSO writes to users/{uid}/tutorialState in Firebase RTDB. The
  //   useAuth() hook is called via a context that wraps TutorialProvider in
  //   AppProviders.js (AuthProvider > ... > TutorialProvider), so this is safe.
  // [TIER 1.1 — Apr 30 2026]: `loading` gates the welcome popup so we don't
  //   force `setShowWelcome(true)` BEFORE the auth listener resolves. In a
  //   private window with no localStorage, the old unconditional 500ms timer
  //   would fire before Firebase merge completed, then dismissing the popup
  //   would clobber the merged `welcome:true` record.
  const { currentUser, loading: authLoading } = useAuth();
  const uid = currentUser?.uid || null;

  // [FIX]: Cache initial state to avoid double localStorage parse
  const [initialState] = useState(() => loadTutorialState());
  // Persistent state: which modules are completed/declined
  const [completedModules, setCompletedModules] = useState(initialState.completedModules);
  const [declinedModules, setDeclinedModules] = useState(initialState.declinedModules);

  // Active tutorial state
  const [activeTutorial, setActiveTutorial] = useState(null);    // module id string
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showWelcome, setShowWelcome] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState(null);       // module id to prompt for

  // Track which tabs have been visited (to avoid re-prompting within a session)
  const visitedTabsRef = useRef(new Set());
  // Guard against concurrent tutorial starts
  const tutorialActiveRef = useRef(false);

  // [P1.4]: Track which uid we've already pulled+merged with so an auth-state
  //   re-render (e.g. re-fetched premium) doesn't re-pull repeatedly. Reset to
  //   null on logout so the next login triggers a fresh merge.
  const mergedForUidRef = useRef(null);
  // [TIER 1.1 — Apr 30 2026]: Mirror the merge-complete sentinel as state so
  //   downstream effects (the welcome-popup gate) can react when the merge
  //   resolves. A ref alone wouldn't trigger a re-render — that's the whole
  //   private-window race: completedModules might not change post-merge (if
  //   remote == local), so the welcome useEffect never re-evaluates.
  const [mergedForUid, setMergedForUid] = useState(null);
  // [P1.4]: Suppress the Firebase write for the very first save effect right
  //   after a merge (the merge already pushed the unioned state — we don't need
  //   to write again on the same render).
  const skipNextRemoteSaveRef = useRef(false);

  // ========================================
  // PERSISTENCE
  // ========================================

  // [P1.4]: On login OR fresh load with an authenticated user, pull tutorial
  //   state from Firebase and merge with whatever's already in component state
  //   (which was seeded from localStorage). Push the merged state back so both
  //   sides are in sync. On logout, reset the merge sentinel so a future login
  //   re-runs the merge for that user.
  useEffect(() => {
    if (!uid) {
      mergedForUidRef.current = null;
      setMergedForUid(null);
      return;
    }
    if (mergedForUidRef.current === uid) return; // already merged this session
    let cancelled = false;
    (async () => {
      const remote = await loadTutorialFromFirebase(uid);
      if (cancelled) return;
      // We need to read the LATEST local state at merge time — completedModules
      // closes over its render snapshot, which is what we want.
      const merged = mergeTutorialState(
        { completedModules, declinedModules },
        remote
      );
      // Only update state if the merge actually changes something (avoids
      // an extra render on every login when nothing's new).
      const changed =
        Object.keys(merged.completedModules).length !== Object.keys(completedModules).length ||
        Object.keys(merged.declinedModules).length !== Object.keys(declinedModules).length ||
        Object.keys(merged.completedModules).some(k => !completedModules[k]) ||
        Object.keys(merged.declinedModules).some(k => !declinedModules[k]);
      if (changed) {
        setCompletedModules(merged.completedModules);
        setDeclinedModules(merged.declinedModules);
      }
      // Push the merged state back to Firebase so the remote catches up. Mark
      // mergedForUidRef BEFORE awaiting so concurrent renders don't double-pull.
      mergedForUidRef.current = uid;
      // [TIER 1.1]: Mirror the ref into state so the welcome-popup useEffect
      //   re-runs even when the merge produced no completedModules change
      //   (e.g. remote returned null AND local was already empty).
      setMergedForUid(uid);
      // Suppress the auto-save effect that's about to fire on the state-change
      // it just triggered — we're already writing here.
      if (changed) skipNextRemoteSaveRef.current = true;
      saveTutorialToFirebase(uid, merged); // graceful — never throws
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  // Save state changes to localStorage (always) AND Firebase (when signed in).
  useEffect(() => {
    saveTutorialState({ completedModules, declinedModules });
    if (uid) {
      if (skipNextRemoteSaveRef.current) {
        skipNextRemoteSaveRef.current = false;
      } else {
        // Fire-and-forget; saveTutorialToFirebase handles failures internally.
        saveTutorialToFirebase(uid, { completedModules, declinedModules });
      }
    }
  }, [completedModules, declinedModules, uid]);

  // ========================================
  // WELCOME POPUP
  // ========================================

  // Auto-show welcome popup for brand-new users (no completed modules at all)
  // [APR 21 2026 FIX]: If a character already exists in autosave storage,
  // the user is a returning user — don't force-show the welcome popup even if
  // tutorial localStorage was cleared (e.g., Ctrl+F5 / cache quirks / fresh
  // tutorial data migration). User reported the welcome kept popping up on
  // every load despite having a loaded character.
  // [TIER 1.1 — Apr 30 2026]: Gate the welcome decision on auth loading. In a
  // private window with no localStorage and no autosave, the unconditional
  // 500ms timer used to fire BEFORE the auth listener resolved and the
  // Firebase merge fetched the user's `welcome:true` record. Once the popup
  // was visible, dismissing it wrote `welcome:true` locally — clobbering the
  // remote merge. The fix: wait until `authLoading === false`, AND for
  // authenticated users wait until the Firebase merge has run (mergedForUidRef
  // matches the current uid). Re-run when those gates flip.
  useEffect(() => {
    // Auth listener still resolving — defer the decision.
    if (authLoading) return;
    // Authenticated users: wait for the Firebase merge to populate
    // completedModules before reading it. Without this guard, the merge race
    // returns: we'd read the stale (empty) completedModules from the initial
    // localStorage load, decide "brand-new user," show the welcome, and the
    // dismissWelcome write would race the merge. We use the mergedForUid
    // STATE (mirror of mergedForUidRef) as the dep so this effect re-fires
    // when the merge resolves — a ref change alone wouldn't trigger it.
    if (uid && mergedForUid !== uid) return;

    const hasAnyCompleted = Object.keys(completedModules).length > 0;

    let hasExistingCharacter = false;
    try {
      const autosaveRaw = localStorage.getItem('tabletopInventory_autosave');
      if (autosaveRaw) {
        const parsed = JSON.parse(autosaveRaw);
        // Meaningful character = has a name in basics OR any class entry
        hasExistingCharacter = Boolean(
          parsed?.playerStats?.basics?.name ||
          parsed?.playerStats?.basics?.race ||
          (Array.isArray(parsed?.playerStats?.classes) && parsed.playerStats.classes.length > 0) ||
          (Array.isArray(parsed?.savedCharacters) && parsed.savedCharacters.length > 0)
        );
      }
      // Also check the savedCharacters key directly (older layout)
      if (!hasExistingCharacter) {
        const savedRaw = localStorage.getItem('savedCharacters');
        if (savedRaw) {
          const saved = JSON.parse(savedRaw);
          hasExistingCharacter = Array.isArray(saved) && saved.length > 0;
        }
      }
    } catch (e) {
      // localStorage parse error — treat as brand-new user (safe default)
      hasExistingCharacter = false;
    }

    if (!hasAnyCompleted && !hasExistingCharacter) {
      // Brand-new user — show welcome
      const timer = setTimeout(() => setShowWelcome(true), 500);
      return () => clearTimeout(timer);
    }

    // Returning user whose tutorial localStorage got cleared: silently mark
    // welcome as complete so subsequent loads are clean and module gating works.
    if (!hasAnyCompleted && hasExistingCharacter) {
      setCompletedModules(prev => ({ ...prev, 'welcome': true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, uid, mergedForUid, completedModules]);

  // ========================================
  // TUTORIAL CONTROL
  // ========================================

  /**
   * Start a specific tutorial module
   * [WHY]: Called by WelcomePopup ("Guide Me") or TutorialPrompt ("Yes, show me")
   */
  const startTutorial = useCallback((moduleId) => {
    const module = TUTORIAL_MODULES[moduleId];
    if (!module || !module.steps || module.steps.length === 0) {
      console.warn(`[TutorialContext] No steps found for module: ${moduleId}`);
      return;
    }

    if (tutorialActiveRef.current) {
      console.warn(`[TutorialContext] Tutorial already active, ignoring start for: ${moduleId}`);
      return;
    }

    // [BATCH 12 / P1 D-21 — APR 2026]: Defense-in-depth premium gate.
    //   The auto-prompt path (handleTabVisit) already gates on
    //   PREMIUM_TUTORIAL_MODULES, but startTutorial itself didn't.
    //   WelcomePopup's "I'm the DM" button called startTutorial
    //   directly so free users got a tour for premium features they
    //   couldn't use. UI now hides the button too (WelcomePopup), but
    //   gating here protects every other caller.
    if (!hasPremium && PREMIUM_TUTORIAL_MODULES.has(moduleId)) {
      console.warn(`[TutorialContext] Module ${moduleId} requires premium; refusing start.`);
      return;
    }

    tutorialActiveRef.current = true;
    setActiveTutorial(moduleId);
    setCurrentStepIndex(0);
    setPendingPrompt(null);
    // [S6]: Track tutorial start for engagement metrics
    trackEvent('tutorial_started', { module: moduleId });

    // [FIX APR 2026 Phase 3C.1]: Ensure the tutorial's home tab is active before
    // the spotlight looks for DOM targets. Without this, starting (say) the
    // "downtimes" tutorial while the user is still on "character" leaves the
    // spotlight unable to find its targets — producing the grey-screen bug.
    const targetTab = MODULE_TO_TAB[moduleId];
    if (targetTab) {
      systemBus.emit(EVENTS.TUTORIAL_REQUEST_TAB, { tab: targetTab });
    }
  }, [hasPremium]);

  /**
   * Get the current step object
   */
  const getCurrentStep = useCallback(() => {
    if (!activeTutorial) return null;
    const module = TUTORIAL_MODULES[activeTutorial];
    if (!module || !module.steps) return null;
    return module.steps[currentStepIndex] || null;
  }, [activeTutorial, currentStepIndex]);

  // [BATCH 12 / P1 D-20 — APR 2026]: Per-step tab switching. Pre-fix the
  //   spotlight assumed every step lived on the module's home tab. The
  //   calendar tutorial's cal-journal step targets an anchor in JournalTab
  //   so the spotlight silently auto-skipped after 1500ms. Steps may now
  //   declare a `tab` field; whenever the active step has one, we emit
  //   TUTORIAL_REQUEST_TAB so App.jsx switches before the spotlight scans.
  useEffect(() => {
    if (!activeTutorial) return;
    const module = TUTORIAL_MODULES[activeTutorial];
    if (!module || !module.steps) return;
    const step = module.steps[currentStepIndex];
    if (step && step.tab) {
      systemBus.emit(EVENTS.TUTORIAL_REQUEST_TAB, { tab: step.tab });
    }
  }, [activeTutorial, currentStepIndex]);

  /**
   * Get total steps and current position
   */
  const getProgress = useCallback(() => {
    if (!activeTutorial) return { current: 0, total: 0 };
    const module = TUTORIAL_MODULES[activeTutorial];
    if (!module || !module.steps) return { current: 0, total: 0 };
    return { current: currentStepIndex + 1, total: module.steps.length };
  }, [activeTutorial, currentStepIndex]);

  /**
   * Complete the active tutorial module
   * [WHY]: Marks module as done so it won't be prompted again
   * [NOTE]: Declared before nextStep/skipTutorial to avoid TDZ (temporal dead zone)
   */
  const completeTutorial = useCallback(() => {
    if (activeTutorial) {
      // [S6]: Track tutorial completion (module that just finished)
      trackEvent('tutorial_completed', { module: activeTutorial });
      setCompletedModules(prev => ({ ...prev, [activeTutorial]: true }));
    }
    setActiveTutorial(null);
    setCurrentStepIndex(0);
    tutorialActiveRef.current = false;
  }, [activeTutorial]);

  /**
   * Advance to the next step, or complete the module if at the end
   * [WHY]: Called by TutorialSpotlight on Next click or wait-for-action completion
   */
  const nextStep = useCallback(() => {
    if (!activeTutorial) return;
    const module = TUTORIAL_MODULES[activeTutorial];
    if (!module || !module.steps) return;

    const nextIndex = currentStepIndex + 1;
    if (nextIndex >= module.steps.length) {
      completeTutorial();
    } else {
      setCurrentStepIndex(nextIndex);
    }
  }, [activeTutorial, currentStepIndex, completeTutorial]);

  /**
   * Go back to the previous step
   * [WHY]: Lets users re-read earlier steps (Back button in tutorial panel)
   */
  const prevStep = useCallback(() => {
    if (!activeTutorial || currentStepIndex <= 0) return;
    setCurrentStepIndex(currentStepIndex - 1);
  }, [activeTutorial, currentStepIndex]);

  /**
   * Skip a step (used for optional steps whose target isn't rendered)
   */
  const skipStep = useCallback(() => {
    nextStep();
  }, [nextStep]);

  /**
   * Skip/cancel the entire active tutorial
   * [WHY]: User clicked "Skip Tutorial" — marks complete so it doesn't re-prompt
   */
  const skipTutorial = useCallback(() => {
    // [S6]: Track tutorial skip with current step for drop-off analysis
    if (activeTutorial) {
      trackEvent('tutorial_skipped', { module: activeTutorial, step: currentStepIndex });
    }
    completeTutorial();
  }, [activeTutorial, currentStepIndex, completeTutorial]);

  /**
   * Dismiss the welcome popup
   * [WHY]: Marks welcome as complete; called by both "Guide Me" and "I've Got This"
   */
  const dismissWelcome = useCallback(() => {
    setShowWelcome(false);
    setCompletedModules(prev => ({ ...prev, 'welcome': true }));
    // [NOTE]: Tutorial start is handled by CharacterCreationWizard via guidedCreation prop
  }, []);

  // ========================================
  // DIALOG / GENERAL PROMPTING
  // ========================================

  /**
   * Prompt a tutorial for a dialog or any non-tab context, if not already completed/declined.
   * [WHY]: Tab tutorials use onTabVisit, but dialog tutorials (Custom Spell Creator, Add Item)
   * need a general trigger mechanism called from useEffect on mount.
   * [INTEGRATION]: Dialog components call promptIfNeeded('module-id') in a useEffect.
   */
  const promptIfNeeded = useCallback((moduleId) => {
    // Don't prompt if a tutorial is already active or prompting
    if (tutorialActiveRef.current || activeTutorial) return;
    if (pendingPrompt) return;

    // Don't prompt if module is completed or declined
    if (completedModules[moduleId] || declinedModules[moduleId]) return;

    // Don't prompt if module has no steps
    const module = TUTORIAL_MODULES[moduleId];
    if (!module || !module.steps || module.steps.length === 0) return;

    // Show the prompt after a brief delay (let dialog content render)
    setTimeout(() => {
      if (!tutorialActiveRef.current && !completedModules[moduleId]) {
        setPendingPrompt(moduleId);
      }
    }, 400);
  }, [activeTutorial, pendingPrompt, completedModules, declinedModules]);

  // ========================================
  // TAB VISIT PROMPTING
  // ========================================

  /**
   * Called when user navigates to a tab. Checks if we should prompt a tutorial.
   * [WHY]: Tab tutorials are prompted on FIRST visit only, if module isn't completed/declined
   */
  const onTabVisit = useCallback((tabId) => {
    // Don't prompt if a tutorial is already active
    if (tutorialActiveRef.current || activeTutorial) return;

    // Don't prompt if we already visited this tab this session
    if (visitedTabsRef.current.has(tabId)) return;
    visitedTabsRef.current.add(tabId);

    // Find the module for this tab
    const moduleId = TAB_TO_MODULE[tabId];
    if (!moduleId) return;

    // [v1.1]: Don't show tutorials for premium features to free users
    if (!hasPremium && PREMIUM_TUTORIAL_MODULES.has(moduleId)) return;

    // Don't prompt if module is completed or declined
    if (completedModules[moduleId] || declinedModules[moduleId]) return;

    // Don't prompt if module has no steps
    const module = TUTORIAL_MODULES[moduleId];
    if (!module || !module.steps || module.steps.length === 0) return;

    // Show the prompt after a brief delay (let tab content render)
    setTimeout(() => {
      // Re-check conditions in case things changed during delay
      if (!tutorialActiveRef.current && !completedModules[moduleId]) {
        setPendingPrompt(moduleId);
      }
    }, 600);
  }, [activeTutorial, completedModules, declinedModules, hasPremium]);

  /**
   * Accept a pending tutorial prompt
   */
  const acceptPrompt = useCallback(() => {
    if (pendingPrompt) {
      startTutorial(pendingPrompt);
    }
  }, [pendingPrompt, startTutorial]);

  /**
   * Decline a pending tutorial prompt
   * [WHY]: "No thanks" marks the module as declined so it won't ask again
   */
  const declinePrompt = useCallback(() => {
    if (pendingPrompt) {
      setDeclinedModules(prev => ({ ...prev, [pendingPrompt]: true }));
      setCompletedModules(prev => ({ ...prev, [pendingPrompt]: true }));
      setPendingPrompt(null);
    }
  }, [pendingPrompt]);

  /**
   * Dismiss prompt without declining (e.g., user navigated away)
   */
  const dismissPrompt = useCallback(() => {
    setPendingPrompt(null);
  }, []);

  // ========================================
  // MODULE STATUS QUERIES
  // ========================================

  /**
   * Check if a specific module is completed
   */
  const isModuleComplete = useCallback((moduleId) => {
    return !!completedModules[moduleId];
  }, [completedModules]);

  /**
   * Check if user is brand new (no completed modules)
   */
  const isNewUser = useCallback(() => {
    return Object.keys(completedModules).length === 0;
  }, [completedModules]);

  // ========================================
  // SETTINGS INTEGRATION
  // ========================================

  /**
   * Reset all tutorials — called from Settings "Reset All Tours" button
   * [WHY]: Lets users re-experience all tutorials from scratch (welcome gate + all modules)
   */
  const resetAllTutorials = useCallback(() => {
    setCompletedModules({});
    setDeclinedModules({});
    visitedTabsRef.current.clear();
    setActiveTutorial(null);
    setCurrentStepIndex(0);
    tutorialActiveRef.current = false;
    setPendingPrompt(null);
    // Show welcome again
    setTimeout(() => setShowWelcome(true), 300);
  }, []);

  /**
   * Replay a single tutorial module — clears only that module's completed flag,
   * preserving all other progress, then starts the tour.
   * [WHY]: Per-module replay so users can re-watch one tour without wiping everything
   *   (unlike resetAllTutorials which is all-or-nothing + triggers welcome).
   * [GUARD]: No-ops if a tutorial is already active (mirrors startTutorial guard).
   */
  const replayModule = useCallback((moduleId) => {
    if (tutorialActiveRef.current) {
      console.warn(`[TutorialContext] Tutorial already active, ignoring replayModule for: ${moduleId}`);
      return;
    }
    // Clear only this module's completed flag (functional update — Rule 6)
    setCompletedModules(prev => {
      const next = { ...prev };
      delete next[moduleId];
      return next;
    });
    // Also clear from declined so a previously-declined module can replay
    setDeclinedModules(prev => {
      const next = { ...prev };
      delete next[moduleId];
      return next;
    });
    // Use a microtask gap so the state update flushes before startTutorial
    // reads completedModules through its own closure (defense-in-depth).
    setTimeout(() => startTutorial(moduleId), 0);
  }, [startTutorial]);

  // ========================================
  // CONTEXT VALUE
  // ========================================

  // [FIX APR 2026 — AUDIT C-4]: Memoize context value to prevent unnecessary consumer re-renders
  const value = useMemo(() => ({
    // Welcome popup
    showWelcome,
    dismissWelcome,

    // Active tutorial
    activeTutorial,
    currentStepIndex,
    getCurrentStep,
    getProgress,
    startTutorial,
    nextStep,
    prevStep,
    skipStep,
    skipTutorial,
    completeTutorial,

    // Tab prompts
    pendingPrompt,
    onTabVisit,
    acceptPrompt,
    declinePrompt,
    dismissPrompt,

    // Dialog/general prompts
    promptIfNeeded,

    // Status queries
    isModuleComplete,
    isNewUser,
    completedModules,

    // Settings
    resetAllTutorials,
    replayModule,
  }), [
    showWelcome,
    dismissWelcome,
    activeTutorial,
    currentStepIndex,
    getCurrentStep,
    getProgress,
    startTutorial,
    nextStep,
    prevStep,
    skipStep,
    skipTutorial,
    completeTutorial,
    pendingPrompt,
    onTabVisit,
    acceptPrompt,
    declinePrompt,
    dismissPrompt,
    promptIfNeeded,
    isModuleComplete,
    isNewUser,
    completedModules,
    resetAllTutorials,
    replayModule,
  ]);

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
}

// ========================================
// HOOK
// ========================================

export function useTutorial() {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
}

export default TutorialContext;
